/**
 * XmlAlertsBanner — üst bantta gösterilen açık alarmlar.
 *
 * Backend `GET /xml/feed/alerts` unresolved uyarıları döner:
 *   - consecutive_failure : 3+ sync üst üste başarısız
 *
 * Uyarı görüldüğünde kullanıcı "Anladım" ile çözebilir (resolved=true).
 */
import { useCallback, useEffect, useState } from "react";
import { AlertOctagon, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return ""; }
};

export default function XmlAlertsBanner() {
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [resolving, setResolving] = useState(new Set());

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await api.get("/xml/feed/alerts");
            setAlerts(data.alerts || []);
        } catch {
            /* sessiz */
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        const t = setInterval(load, 60_000); // her dakika refresh
        return () => clearInterval(t);
    }, [load]);

    const resolve = async (id) => {
        setResolving((s) => new Set([...s, id]));
        try {
            await api.post(`/xml/feed/alerts/${id}/resolve`);
            setAlerts((cur) => cur.filter((a) => a.id !== id));
            toast.success("Uyarı çözüldü.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Uyarı çözülemedi.");
        } finally {
            setResolving((s) => { const c = new Set(s); c.delete(id); return c; });
        }
    };

    if (loading || alerts.length === 0) return null;

    return (
        <div className="space-y-2" data-testid="xml-alerts-banner">
            {alerts.map((a) => (
                <div key={a.id} className="rounded-lg border border-destructive/40 bg-destructive/8 px-4 py-3 flex items-start gap-3" data-testid={`xml-alert-${a.id}`}>
                    <AlertOctagon className="h-4 w-4 text-destructive shrink-0 mt-0.5" strokeWidth={2} />
                    <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-semibold text-destructive">XML Eşleştirme Uyarısı</div>
                        <p className="text-[11.5px] text-foreground/85 mt-0.5 leading-relaxed">{a.message}</p>
                        <div className="text-[10px] text-muted-foreground mt-1 font-mono">{fmtDate(a.created_at)}</div>
                    </div>
                    <button
                        onClick={() => resolve(a.id)}
                        disabled={resolving.has(a.id)}
                        className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1 shrink-0 px-2 py-1 rounded-md hover:bg-muted transition-colors"
                        data-testid={`xml-alert-resolve-${a.id}`}
                    >
                        {resolving.has(a.id) ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
                        Anladım
                    </button>
                </div>
            ))}
        </div>
    );
}
